import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import shortid from 'shortid';
import api from 'api';
import Util from 'utility';
import UnitConvert from 'UnitConverter';
import Loading from 'components/Loading/index';
import { __ } from 'locale';
import RecipeSearch from 'components/RecipeSearch';
import { setData, addFoodGroup, setFoodGroupDescription, addFood, addFoodModification, deleteEmptyFoodGroups, setRecipeTitle } from 'actions/index';

function PanelAddRecipe({ onRecipeAdded, isGastroDailyMenu, isDrinkRegime }) {
    const data = useSelector(state => state);
    const dispatch = useDispatch();
    const history = useHistory();
    const [recipeLoading, setRecipeLoading] = useState(false);
    const [currentRecipe, setCurrentRecipe] = useState(null);

    const showRecipeDetail = (id) => {
        setRecipeLoading(true);
        api('recipes/detail/' + id).then(result => {
            setTimeout(() => {
                setRecipeLoading(false);
                setCurrentRecipe(result.data);
            }, 100);
        });
    };

    const closeRecipeDetail = () => {
        setRecipeLoading(false);
        setCurrentRecipe(null);
    };

    const addToRecipe = (recipeId) => {
        api('recipes/add/' + recipeId).then(result => {
            result = result.data;
            dispatch(deleteEmptyFoodGroups());

            Object.keys(result.groups).forEach(g => {
                const group = result.groups[g];
                dispatch(addFoodGroup({ id: group.group_id, title: group.title, type: group.type, opened: true }));
                dispatch(setFoodGroupDescription({ id: group.group_id, description: group.description }));
            });

            Object.keys(result.foods).forEach(f => {
                const food = result.foods[f];
                const newFoodId = shortid();
                dispatch(addFood({
                    id: newFoodId,
                    food_id: food.food_id,
                    group_id: food.group_id,
                    is_drink: food.is_drink,
                    weight: food.amount,
                    image: 'https://www.zofapp.cz/data/zof/item/' + food.food_id + '.svg',
                    title: data.globals.itemData[food.food_id]['title'],
                }));
                Object.keys(food.modifications).forEach(m => {
                    dispatch(addFoodModification({ foodId: newFoodId, modificationId: food.modifications[m] }));
                });
            });

            const currentRecipeTitle = data.titles[data.activeMealHash];
            if (currentRecipeTitle === '') {
                dispatch(setRecipeTitle({ mealHash: data.activeMealHash, title: result.title }));
            }

            dispatch(setData({ lastOpenedCookBookRecipe: recipeId, lastAddedRecipeTitle: result.title }));
            Util.doLoadEvaluation();
            onRecipeAdded();
        });
    };

    let content;
    if (recipeLoading) {
        content = <Loading />;
    } else if (currentRecipe) {
        const recipe = currentRecipe;
        const ingredients = Object.keys(recipe.ingredients).map(i => {
            const ingr = recipe.ingredients[i];

            let undesirableFoodForProfiles = [];
            if (data.appModeData?.undesirableFoods?.includes(ingr.food_id)) {
                if (typeof data.appModeData.lectureId === 'number') {
                    undesirableFoodForProfiles.push(data.appModeData.profileId);
                }
            } else {
                for (const profileId in data.globals.undesirableFoods) {
                    if (data.globals.undesirableFoods[profileId].includes(ingr.food_id)) {
                        undesirableFoodForProfiles.push(profileId * 1);
                    }
                }
            }

            const itemRowClass = undesirableFoodForProfiles.length > 0
                ? 'flex items-center py-[0.35em] border-b border-[#eee] bg-[#fff5f5]'
                : 'flex items-center py-[0.35em] border-b border-[#eee]';
            const undesirableFoodWarning = undesirableFoodForProfiles.map(profileId => {
                const profile = data.globals.profiles[profileId];
                const profileColor = profile.profile_color || '999999';
                return <span key={profileId} style={{ backgroundColor: '#' + profileColor, color: '#fff' }} className="text-[0.75em] font-bold uppercase px-[4px] py-[2px] rounded mr-[2px]">{profile.name}</span>;
            });

            let ingredientAmount = null;
            if (Util.userHasPurchased('ZOF_COOKBOOK')) {
                const foodItem = {
                    id: ingr['item_id'],
                    selectedWeight: ingr['amount'],
                    weight: data.globals.itemData[ingr['item_id']].weight,
                    norm_unit_amount: data.globals.itemData[ingr['item_id']].norm_unit_amount * 1.0,
                    norm_unit_unit: data.globals.itemData[ingr['item_id']].norm_unit_unit,
                    norm_unit2_amount: data.globals.itemData[ingr['item_id']].norm_unit2_amount * 1.0,
                    norm_unit2_unit: data.globals.itemData[ingr['item_id']].norm_unit2_unit
                };
                const uc = UnitConvert;
                const normalizedUnitInfo = uc.convert(foodItem);
                ingredientAmount = (normalizedUnitInfo && normalizedUnitInfo !== '' && normalizedUnitInfo !== ' null')
                    ? <span>{normalizedUnitInfo}</span>
                    : null;
            }

            return (
                <div className={itemRowClass} key={i}>
                    <div className="shrink-0 mr-[0.75em] w-[36px]">
                        <img src={`https://www.zofapp.cz/data/hra/icon/png/${ingr['icon_url']}`} alt="" />
                    </div>
                    <div className="flex-1">
                        <h2>{ingr['title']}</h2>
                        {ingredientAmount}
                        {undesirableFoodWarning}
                    </div>
                </div>
            );
        });

        const recipeLocked = recipe.user_id !== data.user.id && !Util.userHasPurchased('ZOF_COOKBOOK');

        let preparationInfo, addToRecipeButton = null;
        if (!recipeLocked) {
            const description = Object.values(recipe.groups).map((group, idx) =>
                Object.values(recipe.groups).length > 1 ? group.title + '\n\n' + group.description + '\n\n' : group.description
            ).join('\n');
            preparationInfo = (
                <div className="flex items-start">
                    <div>{Util.parseRecipeDescription(description)}</div>
                </div>
            );
            addToRecipeButton = <button onClick={() => addToRecipe(recipe.id)}><i className="fa fa-plus"></i> {__('Přidat do receptu')}</button>;
        } else {
            preparationInfo = (
                <>
                    <h3>Příprava receptu</h3>
                    <div>Příprava receptu se zobrazí po zakoupení ZOF kuchařky.</div>
                </>
            );
        }

        const cookbookNotBoughtAlert = recipeLocked ? (
            <div className="rounded mb-[1em] p-[1em] flex items-center bg-[#ffc] text-[#774]">
                <i className="fa fa-info-circle text-[1.5em]"></i>
                <p className="m-0 ml-[1em]">Nemáte přístup k receptům ZOF kuchařky. Pro aktivaci této funkce je nutné <a style={{ fontWeight: 'bold', textDecoration: 'underline' }} onClick={() => history.push('/cookbook/buy')}>kuchařku objednat</a>.</p>
            </div>
        ) : null;

        content = (
            <>
                <div className="flex items-center px-[0.75em] py-[0.5em]">
                    <h2>
                        <button onClick={closeRecipeDetail}><i className="fa fa-angle-left"></i></button>
                        {recipe.lang.title}
                    </h2>
                    <div className="ml-auto">{addToRecipeButton}</div>
                </div>
                <div className="px-[0.75em] py-[0.5em]">
                    {cookbookNotBoughtAlert}
                    <div className="flex mt-[1em]">
                        <div style={{flex:1}}>
                            <img style={{width:'100%',height:'auto',display:'block'}} src={`https://www.zofapp.cz/data/zof/recipe/${Math.floor(recipe.id / 1000)}/${recipe.id}.jpg`} alt="" />
                            <h2>{__('Ingredience')} ({parseFloat(recipe.nutrition_data.weight)} g)</h2>
                            <div className="flex flex-col">{ingredients}</div>
                        </div>
                        <div style={{flexBasis:'270px'}}>
                            {preparationInfo}
                        </div>
                    </div>
                </div>
            </>
        );
    } else {
        content = <RecipeSearch onClick={showRecipeDetail} isEmbedded={true} isDrinkRegime={isDrinkRegime} />;
    }

    return (
        <div className="flex flex-col flex-1">
            {content}
        </div>
    );
}

export default PanelAddRecipe;
